import { AdvancedSettingsBar } from "components/Campaign/CampaignEffects/AdvancedSettingsBar/AdvancedSettingsBar";
import { ReactComponent as Pencil02Icon } from "components/Icons/pencil-02.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { FormulaPreview } from "components/Layout/Campaign/Common/CampaignRules/Rule/Effects/EffectReadOnly";
import { BoxElement } from "components/Layout/Segment/SegmentForm/SegmentConditions/BoxElement";
import { useMessage } from "hooks/intl";
import {
  AchievementAggregation,
  AchievementAggregationUnion,
  AchievementCompleteRulePeriod,
  AchievementTriggers,
  AchievementTriggersUnion,
  CompleteRule,
} from "types/achievements";
import { formatNumberValue } from "utilities/numbers";
import { pickPluralizedUnit } from "utilities/strings";
import { useStyles } from "./RuleGoal.styles";
import { getPluralizedTimeframe } from "./utils";

interface Props {
  setFieldValue: any;
  fieldName: string;
  errors: any;
  event?: string;
  trigger: AchievementTriggersUnion;
  completeRule?: CompleteRule & {
    draft?: CompleteRule;
    isEditing?: boolean;
  };
  aggregation: {
    type: AchievementAggregationUnion;
    rule?: string;
  };
}

export const GoalDescription = ({
  event,
  completeRule,
  trigger,
  aggregation,
  errors,
}: {
  event?: string;
  errors?: any;
  trigger: AchievementTriggersUnion;
  completeRule?: CompleteRule & {
    draft?: CompleteRule;
    isEditing?: boolean;
  };
  aggregation?: {
    type: AchievementAggregationUnion;
    rule?: string;
  };
}) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { OVERALL, LAST_DAY, DAY, WEEK, MONTH, YEAR, STREAK } =
    AchievementCompleteRulePeriod;
  const { EXPRESSION, QUANTITY } = AchievementAggregation;
  const { TRANSACTION, CUSTOM_EVENT } = AchievementTriggers;

  const periodType = completeRule?.period?.type;
  const periodGoal = completeRule?.periodGoal;
  const periodValue = completeRule?.period?.value;
  const periodConsecutive = completeRule?.period?.consecutive;
  const streakType = completeRule?.period?.streakType;
  const isTransactionTrigger = trigger === TRANSACTION;
  const isCustomEventTrigger = trigger === CUSTOM_EVENT;
  const isQuantityAggregation = aggregation?.type === QUANTITY;
  const isExpressionAggregation = aggregation?.type === EXPRESSION;

  const overallElement = (
    <span>
      <BoxElement content={message({ id: "overall" })} />
    </span>
  );

  const periodGoalElement = (
    <span>
      <BoxElement
        content={formatNumberValue(periodGoal, "")}
        error={errors?.completeRule?.periodGoal}
      />
    </span>
  );

  const periodValueElement = (
    <span>
      <BoxElement
        content={formatNumberValue(periodValue, "")}
        error={errors?.completeRule?.period?.value}
      />
    </span>
  );

  const consecutiveElement = (
    <span>
      <BoxElement
        content={formatNumberValue(periodConsecutive, "")}
        error={errors?.completeRule?.period?.consecutive}
      />
    </span>
  );

  const expressionFormula = (
    <span className={classes.expressionFormula}>
      <BoxElement
        content={
          !!aggregation?.rule ? (
            <FormulaPreview pointRule={aggregation?.rule} />
          ) : (
            ""
          )
        }
        error={errors?.aggregation?.rule}
      />
    </span>
  );

  const eventElement = event && (
    <span>
      <BoxElement content={event} error={errors?.event} />
    </span>
  );

  const pluralizedTransaction = message({
    id: pickPluralizedUnit(periodGoal as number, "transaction", "transactions"),
  }).toLocaleLowerCase();

  const pluralizedTime = message({
    id: pickPluralizedUnit(periodGoal as number, "time", "times"),
  }).toLocaleLowerCase();

  const pluralizedDay = message({
    id: pickPluralizedUnit(periodValue as number, "day", "days"),
  }).toLocaleLowerCase();

  const pluralizedConsecutiveType = message({
    id: pickPluralizedUnit(
      periodConsecutive as number,
      streakType,
      getPluralizedTimeframe(streakType),
    ),
  }).toLocaleLowerCase();

  const getOverallGoalDescription = () => {
    switch (true) {
      case isTransactionTrigger && isQuantityAggregation:
        return message(
          { id: "overallTransactionQuantityGoal" },
          {
            goal: overallElement,
            periodGoal: periodGoalElement,
            pluralizedTransaction,
          },
        );
      case isTransactionTrigger && isExpressionAggregation:
        return message(
          { id: "overallTransactionExpressionGoal" },
          {
            goal: overallElement,
            expressionFormula,
            periodGoal: periodGoalElement,
          },
        );
      case isCustomEventTrigger && isQuantityAggregation:
        return message(
          { id: "overallCustomEventQuantityGoal" },
          {
            goal: overallElement,
            event: eventElement,
            periodGoal: periodGoalElement,
            pluralizedTime,
          },
        );
      case isCustomEventTrigger && isExpressionAggregation:
        return message(
          { id: "overallCustomEventExpressionGoal" },
          {
            goal: overallElement,
            event: eventElement,
            expressionFormula,
            periodGoal: periodGoalElement,
          },
        );
    }
  };

  const getLastXDaysGoalDescription = () => {
    switch (true) {
      case isTransactionTrigger && isQuantityAggregation:
        return message(
          { id: "lastDayTransactionQuantityGoal" },
          {
            periodValue: periodValueElement,
            pluralizedDay,
            periodGoal: periodGoalElement,
            pluralizedTransaction,
          },
        );
      case isTransactionTrigger && isExpressionAggregation:
        return message(
          { id: "lastDayTransactionExpressionGoal" },
          {
            periodValue: periodValueElement,
            pluralizedDay,
            expressionFormula,
            periodGoal: periodGoalElement,
          },
        );
      case isCustomEventTrigger && isQuantityAggregation:
        return message(
          { id: "lastDayCustomEventQuantityGoal" },
          {
            periodValue: periodValueElement,
            pluralizedDay,
            event: eventElement,
            periodGoal: periodGoalElement,
            pluralizedTime,
          },
        );
      case isCustomEventTrigger && isExpressionAggregation:
        return message(
          { id: "lastDayCustomEventExpressionGoal" },
          {
            periodValue: periodValueElement,
            pluralizedDay,
            event: eventElement,
            expressionFormula,
            periodGoal: periodGoalElement,
          },
        );
    }
  };

  const getConsecutiveGoalDescription = () => {
    switch (true) {
      case isTransactionTrigger && isQuantityAggregation:
        return message(
          { id: "consecutiveTransactionQuantityGoal" },
          {
            periodGoal: periodGoalElement,
            pluralizedTransaction,
            periodType: streakType,
            consecutiveElement,
            pluralizedConsecutiveType,
            span: (chunks: React.ReactNode[]) => <>{chunks}</>,
          },
        );
      case isTransactionTrigger && isExpressionAggregation:
        return message(
          { id: "consecutiveTransactionExpressionGoal" },
          {
            expressionFormula,
            periodGoal: periodGoalElement,
            periodType: streakType,
            consecutiveElement,
            pluralizedConsecutiveType,
            span: (chunks: React.ReactNode[]) => <>{chunks}</>,
          },
        );
      case isCustomEventTrigger && isQuantityAggregation:
        return message(
          { id: "consecutiveCustomEventQuantityGoal" },
          {
            event: eventElement,
            periodGoal: periodGoalElement,
            pluralizedTime,
            periodType: streakType,
            consecutiveElement,
            pluralizedConsecutiveType,
            span: (chunks: React.ReactNode[]) => <>{chunks}</>,
          },
        );
      case isCustomEventTrigger && isExpressionAggregation:
        return message(
          { id: "consecutiveCustomEventExpressionGoal" },
          {
            event: eventElement,
            expressionFormula,
            periodGoal: periodGoalElement,
            periodType: streakType,
            consecutiveElement,
            pluralizedConsecutiveType,
            span: (chunks: React.ReactNode[]) => <>{chunks}</>,
          },
        );
    }
  };

  const getGoalDescription = () => {
    switch (periodType) {
      case OVERALL:
        return getOverallGoalDescription();
      case LAST_DAY:
        return getLastXDaysGoalDescription();
      case STREAK:
        switch (streakType) {
          case DAY:
          case WEEK:
          case MONTH:
          case YEAR:
            return getConsecutiveGoalDescription();
        }
    }
  };

  const goalDescription = getGoalDescription();

  return <div className={classes.readOnlyGoal}>{goalDescription}</div>;
};

export const RuleGoalReadOnly = ({
  completeRule,
  fieldName,
  setFieldValue,
  aggregation,
  trigger,
  errors,
  event,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();

  const customEventTriggerLabels = [
    {
      label: message({ id: "countUniqueCustomEventAttributes" }),
      value: "",
    },
    {
      label: message({ id: "customEventAttribute" }),
      value: completeRule?.uniqueAttribute || "",
    },
  ];

  return (
    <>
      <div className={classes.readOnlyWrapper}>
        <GoalDescription
          completeRule={completeRule}
          event={event}
          errors={errors}
          aggregation={aggregation}
          trigger={trigger}
        />
        <div className={classes.noWrap}>
          <div className={classes.iconBox}>
            <Pencil02Icon
              className={classes.icon}
              onClick={() =>
                setFieldValue(fieldName, {
                  ...completeRule,
                  draft: {
                    period: completeRule?.period,
                    periodGoal: completeRule?.periodGoal,
                    onlyUniqueAttributeValues:
                      completeRule?.onlyUniqueAttributeValues,
                    uniqueAttribute: completeRule?.uniqueAttribute,
                  },
                  isEditing: true,
                })
              }
              data-test-id="edit-goal"
            />
          </div>
          <div className={classes.iconBox}>
            <XCloseIcon
              onClick={() => setFieldValue(fieldName, {})}
              className={classes.icon}
              data-test-id="remove-goal"
            />
          </div>
        </div>
      </div>
      {completeRule?.onlyUniqueAttributeValues && (
        <AdvancedSettingsBar
          variant="text"
          labels={customEventTriggerLabels}
          vectorMarginClass={classes.vectorMargin}
        />
      )}
    </>
  );
};
